/* External dependencies */
import { z } from 'zod';

/* Local dependencies */
import {
    AgreedToTermsRequiredException,
    ConfirmPasswordRequiredException,
    EmailInvalidException,
    EmailRequiredException,
    FamilyNameLatinOnlyException,
    FamilyNameRequiredException,
    GivenNameLatinOnlyException,
    GivenNameRequiredException,
    NewPasswordRequiredException,
    NewPasswordTooShortException,
    PasswordMismatchException,
    PasswordMissingLowercaseException,
    PasswordMissingNumberException,
    PasswordMissingSpecialCharException,
    PasswordMissingUppercaseException,
    PasswordRequiredException,
    PasswordTooShortException,
    PhoneInvalidException,
    PhoneRequiredException,
    VerificationCodeInvalidException,
    VerificationCodeRequiredException
} from './exceptions';

export const loginSchema = z.object({
  email: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), { message: 'invalid' }),

  password: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && val.length >= 8, { message: 'tooShort' })
    .refine((val) => val && /[A-Z]/.test(val), { message: 'missingUppercase' })
    .refine((val) => val && /[a-z]/.test(val), { message: 'missingLowercase' })
    .refine((val) => val && /\d/.test(val), { message: 'missingNumber' })
    .refine((val) => val && /[@$!%*?&#^()_+\-=[\]{};':"\\|,.<>/]/.test(val), { message: 'missingSpecialChar' }),
});

export const registerSchema = z.object({
  email: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), { message: 'invalid' }),

  password: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && val.length >= 8, { message: 'tooShort' })
    .refine((val) => val && /[A-Z]/.test(val), { message: 'missingUppercase' })
    .refine((val) => val && /[a-z]/.test(val), { message: 'missingLowercase' })
    .refine((val) => val && /\d/.test(val), { message: 'missingNumber' })
    .refine((val) => val && /[@$!%*?&#^()_+\-=[\]{};':"\\|,.<>/]/.test(val), { message: 'missingSpecialChar' }),

  confirmPassword: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' }),

  givenName: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => !val || /^[a-zA-Z\s\-']+$/.test(val), { message: 'latinOnly' }),

  familyName: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => !val || /^[a-zA-Z\s\-']+$/.test(val), { message: 'latinOnly' }),

  agreedToTerms: z
    .union([z.boolean(), z.null(), z.undefined()])
    .refine((val) => val === true, { message: 'required' }),

  phone: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && /^\+?[0-9\s\-().]{7,20}$/.test(val), { message: 'invalid' }),
});

export const forgotPasswordEmailSchema = z.object({
  email: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), { message: 'invalid' }),
});

export const forgotPasswordVerifySchema = z.object({
  verificationCode: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && val.length === 6, { message: 'invalid' }),
});

export const forgotPasswordResetSchema = z.object({
  newPassword: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && val.length >= 8, { message: 'tooShort' })
    .refine((val) => val && /[A-Z]/.test(val), { message: 'missingUppercase' })
    .refine((val) => val && /[a-z]/.test(val), { message: 'missingLowercase' })
    .refine((val) => val && /\d/.test(val), { message: 'missingNumber' })
    .refine((val) => val && /[@$!%*?&#^()_+\-=[\]{};':"\\|,.<>/]/.test(val), { message: 'missingSpecialChar' }),

  confirmPassword: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' }),
});

export const verifyCodeSchema = z.object({
  verificationCode: z
    .union([z.string(), z.null(), z.undefined()])
    .refine((val) => val && val.length > 0, { message: 'required' })
    .refine((val) => val && val.length === 6, { message: 'invalid' }),
});

export const authExceptionMap = {
  email: {
    required: () => new EmailRequiredException(),
    invalid: () => new EmailInvalidException(),
  },
  password: {
    required: () => new PasswordRequiredException(),
    tooShort: () => new PasswordTooShortException(),
    missingUppercase: () => new PasswordMissingUppercaseException(),
    missingLowercase: () => new PasswordMissingLowercaseException(),
    missingNumber: () => new PasswordMissingNumberException(),
    missingSpecialChar: () => new PasswordMissingSpecialCharException(),
  },
  confirmPassword: {
    required: () => new ConfirmPasswordRequiredException(),
    mismatch: () => new PasswordMismatchException(),
  },
  givenName: {
    required: () => new GivenNameRequiredException(),
    latinOnly: () => new GivenNameLatinOnlyException(),
  },
  familyName: {
    required: () => new FamilyNameRequiredException(),
    latinOnly: () => new FamilyNameLatinOnlyException(),
  },
  agreedToTerms: {
    required: () => new AgreedToTermsRequiredException(),
  },
  phone: {
    required: () => new PhoneRequiredException(),
    invalid: () => new PhoneInvalidException(),
  },
  verificationCode: {
    required: () => new VerificationCodeRequiredException(),
    invalid: () => new VerificationCodeInvalidException(),
  },
  newPassword: {
    required: () => new NewPasswordRequiredException(),
    tooShort: () => new NewPasswordTooShortException(),
    missingUppercase: () => new PasswordMissingUppercaseException(),
    missingLowercase: () => new PasswordMissingLowercaseException(),
    missingNumber: () => new PasswordMissingNumberException(),
    missingSpecialChar: () => new PasswordMissingSpecialCharException(),
  },
} as const;

export const authSchemaMap = {
  login: loginSchema,
  register: registerSchema,
  forgotPassword: forgotPasswordEmailSchema,
  forgotPasswordVerify: forgotPasswordVerifySchema,
  forgotPasswordReset: forgotPasswordResetSchema,
  verify: verifyCodeSchema,
} as const;

export type AuthFormType = keyof typeof authSchemaMap;
